import { Link } from "react-router-dom";
import Header from "@/components/Header";
import { Trophy, Users, Sparkles, Heart, Shield, Brain } from "lucide-react";

export default function WhoWeAre() {
  return (
    <div className="min-h-screen bg-appbg text-white">
      <Header />

      {/* Hero */}
      <section className="px-6 md:px-12 lg:px-24 py-16 md:py-24 border-b border-neutral-900">
        <div className="max-w-5xl mx-auto">
          <div className="text-mono text-blaze text-xs tracking-[0.3em] mb-6" data-testid="wwa-eyebrow">
            WHO WE ARE
          </div>
          <h1 className="heading text-4xl sm:text-5xl lg:text-6xl leading-[1.05]" data-testid="wwa-headline">
            Built by <span className="text-blaze">coaches</span>.<br />
            Shaped by <span className="text-volt">leaders</span>.<br />
            Created to help schools and coaches find the <span className="text-blaze">right fit</span>.
          </h1>
          <p className="text-neutral-200 text-lg sm:text-xl leading-relaxed mt-8 max-w-3xl" data-testid="wwa-lede">
            CoachConnect was not born in a conference room. It grew out of a lifetime spent on practice fields,
            inside team huddles, and alongside athletic programs trying to build something bigger than a roster.
          </p>
        </div>
      </section>

      {/* The story — as told by the founder */}
      <section className="px-6 md:px-12 lg:px-24 py-16 md:py-20 border-b border-neutral-900">
        <div className="max-w-4xl mx-auto">
          <div className="text-mono text-blaze text-xs tracking-[0.3em] mb-6" data-testid="wwa-story-eyebrow">
            THE STORY · IN OUR OWN WORDS
          </div>
          <div className="border-l-4 border-blaze pl-6 md:pl-10 space-y-6" data-testid="wwa-story">
            <p className="text-neutral-100 text-lg md:text-xl leading-relaxed">
              I grew up playing lacrosse in the Northeast, where the sport helped open the door to my dream school and the opportunity to play <span className="text-blaze font-medium">four years of Division I lacrosse</span>. I went on to coach high school lacrosse for <span className="text-blaze font-medium">more than 30 years</span>, helping build programs, develop young athletes, and win <span className="text-blaze font-medium">multiple State Championships</span> along the way.
            </p>
            <p className="text-neutral-100 text-lg md:text-xl leading-relaxed">
              My son followed a similar path — a <span className="text-blaze font-medium">High School All-American</span>, a state champion, and a college lacrosse player at <span className="text-blaze font-medium">both the Division I and Division III levels</span>. Together, we have lived the sport as players, coaches, parents, leaders, and program builders.
            </p>
            <p className="text-neutral-100 text-lg md:text-xl leading-relaxed">
              Beyond the high school game, I founded and ran a <span className="text-blaze font-medium">club lacrosse program for more than 15 years</span>, helping nearly <span className="text-blaze font-medium">100 players</span> move on to the next level — prep school, Division I, Division II, Division III, and MCLA. Season after season, we saw what the right coach can do for a young athlete. We also saw what happens when the wrong hire is made — or when the right coach is never found.
            </p>
            <p className="text-neutral-100 text-lg md:text-xl leading-relaxed">
              As a veteran, coach, and lifelong leader, I believe <span className="text-blaze font-medium">leadership, trust, character, and fit</span> matter just as much as a résumé. The best coach is not always the loudest name or the easiest candidate to find. The best coach is the one who aligns with the <span className="text-blaze font-medium">culture, values, expectations, and mission</span> of the program.
            </p>
            <p className="text-neutral-100 text-lg md:text-xl leading-relaxed">
              We also know that <span className="text-volt font-medium">fit goes both ways</span>. For young coaches trying to break into the profession, and for established coaches looking for the next right opportunity, the decision is not just about a job title. It is about <span className="text-volt font-medium">growth, family, location, culture, stability, mentorship, and the chance to make a real impact</span>.
            </p>
            <p className="text-neutral-100 text-lg md:text-xl leading-relaxed">
              After decades on every side of a coaching search — applying, interviewing, hiring, recommending, and watching schools and organizations struggle through unclear processes — we saw the same problem again and again: <span className="text-white font-medium">good coaches get missed, good programs settle</span>, and too many searches depend on word of mouth, limited networks, or incomplete information.
            </p>
            <p className="text-neutral-100 text-xl md:text-2xl leading-relaxed font-medium" data-testid="wwa-story-mission">
              CoachConnect was created to change that.
            </p>
            <p className="text-neutral-100 text-lg md:text-xl leading-relaxed">
              We help schools, athletic directors, and organizations identify coaches who are not just <span className="text-blaze font-medium">available</span>, but truly <span className="text-blaze font-medium">aligned</span>. And we help coaches — from those just starting out to those with years of experience — find opportunities that fit their <span className="text-volt font-medium">goals, values, careers, and families</span>.
            </p>
          </div>
        </div>
      </section>

      {/* Mantra strip — the promise, three-part */}
      <section className="px-6 md:px-12 lg:px-24 py-14 md:py-16 border-b border-neutral-900 relative" data-testid="wwa-mantra">
        <div className="absolute top-0 left-0 h-1 w-1/3 bg-gradient-to-r from-blaze to-volt" />
        <div className="max-w-5xl mx-auto text-center">
          <div className="text-mono text-blaze text-[10px] tracking-[0.4em] mb-6">OUR PROMISE · BOTH SIDES OF THE SIDELINE</div>
          <div className="heading text-3xl sm:text-5xl lg:text-6xl leading-tight">
            The <span className="text-blaze">right coach</span>,<br />
            for the <span className="text-volt">right program</span>,<br />
            at the <span className="text-white">right time</span>.
          </div>
        </div>
      </section>

      {/* The arena — Roosevelt callout */}
      <section
        className="px-6 md:px-12 lg:px-24 py-14 md:py-16 border-b border-neutral-900 bg-appcard"
        data-testid="wwa-arena-section"
      >
        <div className="max-w-4xl mx-auto">
          <div className="text-mono text-blaze text-[10px] tracking-[0.4em] mb-4">
            OUR NORTH STAR · TEDDY ROOSEVELT · CITIZENSHIP IN A REPUBLIC, 1910
          </div>
          <blockquote className="border-l-4 border-blaze pl-6 md:pl-8">
            <p className="text-neutral-100 text-lg md:text-xl leading-relaxed">
              &ldquo;The credit belongs to the <span className="text-blaze font-medium">man who is actually in the arena</span>,
              whose face is marred by dust and sweat and blood; who strives valiantly; who errs,
              who comes short again and again, because there is no effort without error and shortcoming …
              who at the best knows in the end the triumph of high achievement, and who at the worst, if he fails,
              at least fails while <span className="text-blaze font-medium">daring greatly</span>.&rdquo;
            </p>
          </blockquote>
          <p className="text-sm text-neutral-300 mt-6 leading-relaxed">
            Coaching is arena work — literally. We built this platform for the coaches who step onto the field
            season after season, and for the programs willing to back them. Every feature — the CIR, the anonymity,
            the concierge intros — protects the people who do the work.
          </p>
        </div>
      </section>

      {/* What we believe */}
      <section className="px-6 md:px-12 lg:px-24 py-16 md:py-20 border-b border-neutral-900">
        <div className="max-w-5xl mx-auto">
          <div className="text-mono text-blaze text-xs tracking-[0.3em] mb-6" data-testid="wwa-beliefs-eyebrow">
            WHAT WE BELIEVE
          </div>
          <h2 className="heading text-3xl sm:text-4xl mb-12 leading-tight" data-testid="wwa-beliefs-headline">
            Three things that drove every decision.
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-appcard p-7 border-l-4 border-blaze hover:shadow-[0_0_0_1px_rgb(var(--app-primary-rgb))] transition-all" data-testid="wwa-belief-1">
              <Trophy className="text-blaze mb-4" size={28} strokeWidth={1.75} />
              <div className="heading text-xl mb-3">Fit goes both ways.</div>
              <p className="text-neutral-200 text-base leading-relaxed">
                A program&apos;s identity is its coach. And a coach&apos;s life is more than a job title. We designed CoachConnect to match on
                <span className="text-white font-medium"> culture, values, family, and mission</span> — not just résumés.
              </p>
            </div>
            <div className="bg-appcard p-7 border-l-4 border-volt hover:shadow-[0_0_0_1px_rgb(var(--app-accent-rgb))] transition-all" data-testid="wwa-belief-2">
              <Shield className="text-volt mb-4" size={28} strokeWidth={1.75} />
              <div className="heading text-xl mb-3">Anonymity is sacred.</div>
              <p className="text-neutral-200 text-base leading-relaxed">
                A great coach exploring options shouldn&apos;t have to risk their current role. An AD running a stealth search shouldn&apos;t have to
                tip their hand. <span className="text-white font-medium">Mutual discretion</span>, always — until both sides say otherwise.
              </p>
            </div>
            <div className="bg-appcard p-7 border-l-4 border-blaze hover:shadow-[0_0_0_1px_rgb(var(--app-primary-rgb))] transition-all" data-testid="wwa-belief-3">
              <Sparkles className="text-blaze mb-4" size={28} strokeWidth={1.75} />
              <div className="heading text-xl mb-3">AI assists. Coaches decide.</div>
              <p className="text-neutral-200 text-base leading-relaxed">
                Seven specialized AI agents do the grunt work — sourcing, vetting, drafting offers — so the human conversations
                (the ones that actually matter) get the time they deserve.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* The covenants */}
      <section className="px-6 md:px-12 lg:px-24 py-16 md:py-20 border-b border-neutral-900">
        <div className="max-w-4xl mx-auto">
          <div className="text-mono text-blaze text-xs tracking-[0.3em] mb-6" data-testid="wwa-covenants-eyebrow">
            OUR COVENANTS TO YOU
          </div>
          <ul className="space-y-5" data-testid="wwa-covenants-list">
            {[
              { icon: Users, color: "blaze", t: "Coaches never pay.", d: "Free to sign up, build your profile, see every match in full and reply to programs. Privacy controls are yours regardless." },
              { icon: Shield, color: "volt", t: "We never reveal an identity without consent.", d: "No exception. No \u2018we\u2019ll just hint at it.\u2019 Both sides have to opt in to every reveal." },
              { icon: Heart, color: "blaze", t: "We answer our own email.", d: "There\u2019s no support call center. If you need us, you reach us \u2014 usually within a few hours." },
              { icon: Brain, color: "volt", t: "We will not sell your data.", d: "Not to recruiters, not to coach databases, not to the next AI tool. The platform\u2019s value is the trust \u2014 we won\u2019t trade it." },
            ].map((c, i) => (
              <li key={i} className={`flex items-start gap-5 border-l-4 pl-5 ${c.color === "blaze" ? "border-blaze" : "border-volt"}`} data-testid={`wwa-covenant-${i+1}`}>
                <c.icon className={c.color === "blaze" ? "text-blaze flex-shrink-0 mt-1" : "text-volt flex-shrink-0 mt-1"} size={22} strokeWidth={1.75} />
                <div>
                  <div className="heading text-xl text-white">{c.t}</div>
                  <p className="text-neutral-200 text-base leading-relaxed mt-1">{c.d}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 md:px-12 lg:px-24 py-16 md:py-20">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="heading text-3xl sm:text-4xl leading-tight" data-testid="wwa-cta-headline">
            Ready to match with a <span className="text-blaze">great coach</span> — or a <span className="text-volt">great program</span>?
          </h2>
          <p className="text-neutral-200 text-lg mt-4">
            Free for coaches. Free job posting for programs. Program Recruiting is $5/month per program.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-10">
            <Link
              to="/auth"
              data-testid="wwa-cta-signup"
              className="px-8 py-4 bg-blaze text-white heading text-lg tracking-wider hover:glow-blaze transition-all"
            >
              Get Started Free
            </Link>
            <Link
              to="/cir"
              data-testid="wwa-cta-cir"
              className="px-8 py-4 border-2 border-volt text-volt heading text-lg tracking-wider hover:bg-volt hover:text-white transition-all"
            >
              See How CIR&trade; Works
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
